import { useContext, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { stripePromise } from '@/common/stripe/stripeClient';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { ALL_PLANS } from '@/pages/customer/pawall/Paywall.constants';
import StripePaymentForm from '@/pages/customer/pawall/subscription/stripe/components/StripePaymentForm';
import { useStripeCheckout } from '@/pages/customer/pawall/subscription/stripe/hooks/useStripeCheckout';
import { useSecuredPaywallLock } from './hooks/useSecuredPaywallLock';
import {
	HERO_IMAGE,
	PLAN_MARKS,
	PLAN_DURATION_KEYS,
	PLAN_BENEFIT_KEYS,
} from './constants/planCheckout.constants';
import {
	sortPlansPremiumFirst,
	getDefaultPremiumPlanName,
} from './helpers/planCheckout.helpers';
import {
	BackgroundBadge,
	CheckoutFormWrap,
	CheckoutHeader,
	CheckoutPanel,
	CheckoutTitle,
	ModalBackground,
	PanelSubtitle,
	PanelTitle,
	PlanBenefitItem,
	PlanBenefits,
	PlanBody,
	PlanCardsStack,
	PlanIcon,
	PlanMeta,
	PlanModalClose,
	PlanModalOverlay,
	PlanModalShell,
	PlanName,
	PlanPrice,
	PlansContent,
	PlansPanel,
	PaymentStatusBox,
	WowPlanCard,
} from './PlanCheckoutModal.styles';

const PlanCheckoutModal = ({ secured = false, onClose, returnPath = '/studio' }) => {
	const { lang } = useContext(LanguageContext);
	const { user } = useGuestAuth();
	const t = useLanguageData('chat');
	const plans = sortPlansPremiumFirst(ALL_PLANS[lang] || ALL_PLANS.en);

	const [selectedPlanName, setSelectedPlanName] = useState(() =>
		getDefaultPremiumPlanName(plans),
	);
	const selectedPlan = plans.find((plan) => plan.name === selectedPlanName);
	const { options: elementsOptions } = useStripeCheckout({
		selectedPlan,
		lang,
	});

	useSecuredPaywallLock(secured);

	return (
		<PlanModalOverlay
			$secured={secured}
			role="dialog"
			aria-modal="true"
			aria-labelledby="planCheckoutTitle"
			aria-hidden="false"
			onClick={secured ? undefined : onClose}
		>
			<PlanModalShell onClick={(event) => event.stopPropagation()}>
				{!secured && onClose && (
					<PlanModalClose
						type="button"
						aria-label={t.planCheckoutClose}
						onClick={onClose}
					>
						×
					</PlanModalClose>
				)}

				<ModalBackground aria-hidden="true">
					<img src={HERO_IMAGE} alt="" />
				</ModalBackground>
				<BackgroundBadge>{t.planPaywallHeroTitle}</BackgroundBadge>

				<PlansPanel>
					<PlansContent>
						<PanelTitle id="planCheckoutTitle">{t.planPaywallTitle}</PanelTitle>
						<PanelSubtitle>{t.planPaywallDescription}</PanelSubtitle>

						<PlanBenefits>
							{PLAN_BENEFIT_KEYS.map((key) => (
								<PlanBenefitItem key={key}>{t[key]}</PlanBenefitItem>
							))}
						</PlanBenefits>

						<PlanCardsStack role="radiogroup" aria-label={t.planPaywallSelectPlan}>
							{plans.map((plan) => {
								const isSelected = selectedPlanName === plan.name;
								const durationKey = PLAN_DURATION_KEYS[plan.name];
								const durationLabel = durationKey ? t[durationKey] : plan.features;

								return (
									<WowPlanCard
										key={plan.name}
										type="button"
										role="radio"
										aria-checked={isSelected}
										$selected={isSelected}
										$popular={plan.isPopular}
										$popularLabel={t.planPaywallPopular}
										onClick={() => setSelectedPlanName(plan.name)}
									>
										<PlanIcon $selected={isSelected}>
											{PLAN_MARKS[plan.name] || plan.name[0]}
										</PlanIcon>
										<PlanBody>
											<PlanName>{plan.name}</PlanName>
											<PlanMeta>{durationLabel}</PlanMeta>
										</PlanBody>
										<PlanPrice>{plan.price}</PlanPrice>
									</WowPlanCard>
								);
							})}
						</PlanCardsStack>
					</PlansContent>
				</PlansPanel>

				<CheckoutPanel aria-labelledby="planCheckoutPaymentTitle">
					<CheckoutHeader>
						<CheckoutTitle id="planCheckoutPaymentTitle">
							{t.planCheckoutPaymentLabel}
						</CheckoutTitle>
					</CheckoutHeader>

					<CheckoutFormWrap>
						{!stripePromise && (
							<PaymentStatusBox $error>
								{t.planCheckoutStripeMissing}
							</PaymentStatusBox>
						)}

						{!user?.id && (
							<PaymentStatusBox>{t.planCheckoutLoginRequired}</PaymentStatusBox>
						)}

						{user?.id && stripePromise && elementsOptions && selectedPlan && (
							<Elements
								key={selectedPlan.name}
								stripe={stripePromise}
								options={elementsOptions}
							>
								<StripePaymentForm
									selectedPlan={selectedPlan}
									returnPath={returnPath}
								/>
							</Elements>
						)}
					</CheckoutFormWrap>
				</CheckoutPanel>
			</PlanModalShell>
		</PlanModalOverlay>
	);
};

export default PlanCheckoutModal;
